import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, X, AlertCircle, Lock, Target, Grid2X2, List, CalendarRange } from 'lucide-react';
import CreateEventModal from '../components/CreateEventModal';
import EditEventModal from '../components/EditEventModal';
import EventCard from '../components/event/EventCard';
import EventsTable from '../components/event/EventsTable';
import TransferEventModal from '../components/event/TransferEventModal';
import QrModal from '../components/event/QrModal';
import SimpleToast from '../components/ui/SimpleToast';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { API_PATH, request } from '../config/api';
import { useAuth } from '../store/AuthContext';
import { useConfirm } from '../store/ConfirmDialogContext';
import { useToast } from '../hooks/useToast';
import { getEventStatus } from '../utils/eventHelpers';
import { exportEventToExcel } from '../utils/exportResults';
import styles from './Dashboard.module.css';

const API_URL = `${API_PATH}/events`;
const APP_URL = window.location.origin;
const EVENT_VIEW_STORAGE_KEY = 'livepulse-events-page-view';

const EventsPage = () => {
    const navigate = useNavigate();
    const confirm = useConfirm();
    const { user } = useAuth();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [events, setEvents] = useState([]);
    const [isFetching, setIsFetching] = useState(true);
    const [search, setSearch] = useState('');
    const { toast, showToast } = useToast();
    const [qrEvent, setQrEvent] = useState(null);
    const [transferEvent, setTransferEvent] = useState(null);
    const [exportingEventId, setExportingEventId] = useState(null);
    const [duplicatingEventId, setDuplicatingEventId] = useState(null);
    const [eventView, setEventView] = useState(() => {
        try {
            return localStorage.getItem(EVENT_VIEW_STORAGE_KEY) === 'list' ? 'list' : 'grid';
        } catch {
            return 'grid';
        }
    });

    const canCreateEvents = user?.canCreateEvents !== false;

    useEffect(() => {
        try {
            localStorage.setItem(EVENT_VIEW_STORAGE_KEY, eventView);
        } catch {
            // View preference is optional when storage is unavailable.
        }
    }, [eventView]);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const res = await request(API_URL);
                if (res.ok) {
                    setEvents(await res.json());
                } else {
                    const data = await res.json().catch(() => ({}));
                    showToast(data.error || 'Failed to load events. Please refresh.');
                }
            } catch (err) {
                console.error('Failed to fetch events', err);
                showToast('Failed to load events. Please refresh.');
            } finally {
                setIsFetching(false);
            }
        };
        fetchEvents();
    }, []);

    const handleEventCreated = (e) => setEvents(prev => [e, ...prev]);
    const handleEventUpdated = (e) => setEvents(prev => prev.map(x => x.id === e.id ? e : x));

    const handleDelete = async (id) => {
        const ok = await confirm({
            title: 'Delete Event',
            message: 'Delete this event and all its activities?',
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setDeletingId(id);
        try {
            const res = await request(`${API_URL}/${id}`, { method: 'DELETE' });
            if (res.ok) {
                setEvents(prev => prev.filter(e => e.id !== id));
                showToast('Event deleted');
            } else {
                const data = await res.json().catch(() => ({}));
                showToast(data.error || 'Failed to delete event');
            }
        } catch (err) {
            console.error('Failed to delete event', err);
            showToast('Failed to delete event');
        } finally {
            setDeletingId(null);
        }
    };

    const handleDuplicate = async (event) => {
        if (!event?.id) return;
        setDuplicatingEventId(event.id);
        try {
            const copyName = (event.name || 'Event').trim().toLowerCase().endsWith(' (copy)')
                ? `${event.name} (2)`
                : `${event.name} (Copy)`;
            const res = await request(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: copyName,
                    startDate: event.start_date,
                    endDate: event.end_date,
                }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                showToast(data.error || 'Failed to duplicate event');
                return;
            }
            const newEvent = await res.json();
            const activitiesRes = await request(`${API_URL}/${event.id}/activities`);
            let failedCount = 0;
            let totalCount = 0;
            let activitiesFetchFailed = false;
            if (activitiesRes.ok) {
                const activities = await activitiesRes.json();
                const list = Array.isArray(activities) ? activities : [];
                totalCount = list.length;
                const baseUrl = `${API_URL}/${newEvent.id}/activities`;
                for (const activity of list) {
                    const title = activity.title || 'Poll';
                    const createRes = await request(baseUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ type: activity.type, title }),
                    });
                    if (!createRes.ok) { failedCount++; continue; }
                    const newActivity = await createRes.json();
                    const content = activity.content && typeof activity.content === 'object' ? JSON.parse(JSON.stringify(activity.content)) : null;
                    if (content) {
                        await request(`${baseUrl}/${newActivity.id}`, {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ content }),
                        });
                    }
                }
            } else {
                activitiesFetchFailed = true;
            }
            setEvents(prev => [newEvent, ...prev]);
            if (activitiesFetchFailed) {
                showToast('Event duplicated, but its polls could not be copied. Add them manually.');
            } else if (failedCount > 0) {
                showToast(`Event duplicated; ${totalCount - failedCount} of ${totalCount} poll(s) copied — ${failedCount} failed.`);
            } else {
                showToast('Event duplicated with all polls!');
            }
        } catch (err) {
            console.error('Failed to duplicate event', err);
            showToast('Failed to duplicate event');
        } finally {
            setDuplicatingEventId(null);
        }
    };

    const handleExportCsv = useCallback(async (event) => {
        if (!event?.id || !event?.code) return;
        setExportingEventId(event.id);
        try {
            const res = await request(`${API_URL}/${event.id}/export`);
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                showToast(data.error || 'Export failed');
                return;
            }
            const data = await res.json();
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 8);
            exportEventToExcel(data, `event-${event.code}-${timestamp}.xlsx`);
            showToast('Export downloaded');
        } catch (err) {
            showToast('Export failed');
        } finally {
            setExportingEventId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = events.filter(e =>
        e.name?.toLowerCase().includes(search.toLowerCase()) ||
        e.code?.toLowerCase().includes(search.toLowerCase())
    );
    const activeEvents = filtered.filter(e => getEventStatus(e).color === 'active');
    const top3ActiveCards = activeEvents.slice(0, 3);
    const tableEvents = filtered.filter(e => !top3ActiveCards.includes(e));

    return (
        <div className={styles.page}>
            {toast && <SimpleToast message={toast} />}
            {qrEvent && (
                <QrModal
                    eventName={qrEvent.name}
                    eventCode={qrEvent.code}
                    joinUrl={`${APP_URL}/join/${qrEvent.code}`}
                    onClose={() => setQrEvent(null)}
                />
            )}
            {transferEvent && (
                <TransferEventModal
                    event={transferEvent}
                    onClose={() => setTransferEvent(null)}
                    onToast={showToast}
                />
            )}

            {user && !canCreateEvents && user.role === 'admin' && (
                <div className={styles.accessBanner}>
                    <AlertCircle size={20} />
                    <span>Your admin access has expired. You cannot create or manage events. Contact superadmin to renew.</span>
                </div>
            )}

            <div className={`${styles.eventsPageHeader} ${styles.eventsHeroHeader}`}>
                <div className={styles.eventsPageHeaderLeft}>
                    <span className={styles.eventsEyebrow}>LivePulse workspace</span>
                    <div className={styles.eventsPageTitleRow}>
                        <span className={styles.eventsHeaderIcon} aria-hidden>
                            <CalendarRange size={24} />
                        </span>
                        <h2 className={styles.eventsPageTitle}>Events</h2>
                        {!isFetching && canCreateEvents && (
                            <Badge variant="neutral" size="md" className={styles.sectionCount}>{filtered.length}</Badge>
                        )}
                    </div>
                    <p className={styles.eventsPageSubtitle}>
                        {canCreateEvents
                            ? 'Open any event to manage interactions and view live results.'
                            : 'Event access is restricted until your account is approved or renewed.'}
                    </p>
                </div>
                {canCreateEvents && (
                    <div className={styles.eventsPageHeaderRight}>
                        <div className={styles.searchWrap}>
                            <Search size={14} className={styles.searchIcon} />
                            <input
                                className={styles.searchInput}
                                placeholder="Search by name or code…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                            {search && (
                                <button className={styles.searchClear} onClick={() => setSearch('')} aria-label="Clear search">
                                    <X size={13} />
                                </button>
                            )}
                        </div>
                        <Button
                            className={styles.createBtn}
                            onClick={() => setShowCreateModal(true)}
                            icon={<Plus size={18} />}
                        >
                            New Event
                        </Button>
                    </div>
                )}
            </div>

            {isFetching ? (
                <div className={styles.eventsGrid}>
                    {[1, 2, 3].map(i => (
                        <div key={i} className={styles.skeletonCard}>
                            <div className={styles.skeletonAccent} />
                            <div className={styles.skeletonBody}>
                                <div className={styles.shimmer} style={{ height: 18, width: '65%' }} />
                                <div className={styles.shimmer} style={{ height: 13, width: '40%', marginTop: 8 }} />
                                <div className={styles.shimmer} style={{ height: 32, width: '55%', marginTop: 12 }} />
                            </div>
                            <div className={styles.skeletonFooter}>
                                <div className={styles.shimmer} style={{ height: 30, flex: 1 }} />
                                <div className={styles.shimmer} style={{ height: 30, flex: 1 }} />
                                <div className={styles.shimmer} style={{ height: 30, flex: 1 }} />
                            </div>
                        </div>
                    ))}
                </div>
            ) : !canCreateEvents ? (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIconWrap}>
                        <Lock size={32} className={styles.emptyIconGlyph} aria-hidden />
                    </div>
                    <h3 className={styles.emptyTitle}>Access restricted</h3>
                    <p className={styles.emptyDesc}>You cannot view or manage events until your account is approved or renewed.</p>
                </div>
            ) : filtered.length === 0 ? (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIllustration}>
                        <div className={styles.emptyIconWrap}>
                            <Target size={32} className={styles.emptyIconGlyph} aria-hidden />
                        </div>
                    </div>
                    <h3 className={styles.emptyTitle}>
                        {search ? 'No matches found' : 'No events yet'}
                    </h3>
                    <p className={styles.emptyDesc}>
                        {search
                            ? `No events match "${search}". Try a different name or code.`
                            : 'Create your first event and start engaging your audience with live polls, Q&A, quizzes and more.'}
                    </p>
                    {!search && canCreateEvents && (
                        <Button
                            className={styles.emptyBtn}
                            icon={<Plus size={18} />}
                            onClick={() => setShowCreateModal(true)}
                        >
                            Create your first event
                        </Button>
                    )}
                    {search && (
                        <Button
                            variant="secondary"
                            className={styles.emptyBtn}
                            icon={<X size={16} />}
                            onClick={() => setSearch('')}
                        >
                            Clear search
                        </Button>
                    )}
                </div>
            ) : (
                <>
                    {top3ActiveCards.length > 0 && (
                        <div className={styles.cardsSection}>
                            <div className={styles.sectionHeader}>
                                <h3 className={styles.cardsSectionTitle}>Active now</h3>
                                <div className={styles.viewToggle} role="group" aria-label="Active event view">
                                    <button
                                        type="button"
                                        className={`${styles.viewToggleButton} ${eventView === 'grid' ? styles.viewToggleButtonActive : ''}`}
                                        onClick={() => setEventView('grid')}
                                        aria-label="Show active events as cards"
                                        aria-pressed={eventView === 'grid'}
                                    >
                                        <Grid2X2 size={15} aria-hidden />
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.viewToggleButton} ${eventView === 'list' ? styles.viewToggleButtonActive : ''}`}
                                        onClick={() => setEventView('list')}
                                        aria-label="Show active events as a list"
                                        aria-pressed={eventView === 'list'}
                                    >
                                        <List size={16} aria-hidden />
                                    </button>
                                </div>
                            </div>
                            <div className={`${styles.eventsGrid} ${eventView === 'list' ? styles.eventsList : ''}`}>
                                {top3ActiveCards.map(event => (
                                    <EventCard
                                        key={event.id}
                                        event={event}
                                        view={eventView}
                                        onEdit={setEditingEvent}
                                        onDelete={handleDelete}
                                        onDuplicate={handleDuplicate}
                                        onTransfer={setTransferEvent}
                                        onExportCsv={event.hasResponses ? handleExportCsv : undefined}
                                        onQr={setQrEvent}
                                        onToast={showToast}
                                        deletingId={deletingId}
                                        exportingEventId={exportingEventId}
                                        duplicatingEventId={duplicatingEventId}
                                        onClick={() => navigate(`/events/${event.id}`)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {tableEvents.length > 0 && (
                        <div className={styles.tableSection}>
                            <h3 className={styles.tableSectionTitle}>
                                {top3ActiveCards.length > 0 ? 'All other events' : 'Your events'}
                            </h3>
                            <EventsTable
                                events={tableEvents}
                                onOpenEvent={(id) => navigate(`/events/${id}`)}
                                onQr={setQrEvent}
                                onEdit={setEditingEvent}
                                onDelete={handleDelete}
                                onDuplicate={handleDuplicate}
                                onTransfer={setTransferEvent}
                                onExportCsv={handleExportCsv}
                                onToast={showToast}
                                deletingId={deletingId}
                                exportingEventId={exportingEventId}
                                duplicatingEventId={duplicatingEventId}
                            />
                        </div>
                    )}
                </>
            )}

            <CreateEventModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreateEvent={handleEventCreated}
            />
            <EditEventModal
                isOpen={!!editingEvent}
                event={editingEvent}
                onClose={() => setEditingEvent(null)}
                onUpdated={handleEventUpdated}
            />
        </div>
    );
};

export default EventsPage;
